/**
 * Offline persistence: IndexedDB holds (a) a local mirror of points for
 * reads when the network is down, and (b) an outbox of pending SyncChanges
 * keyed by point id — the newest queued edit for a point always wins.
 */
import type { PointDto, SyncChange } from '@datum/shared'

const DB_NAME = 'datum'
const DB_VERSION = 1
const POINTS_STORE = 'points'
const OUTBOX_STORE = 'outbox'
const KV_STORE = 'kv'

export interface OutboxEntry {
  pointId: string
  change: SyncChange
  queuedAt: number
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION)
      request.onupgradeneeded = () => {
        const db = request.result
        if (!db.objectStoreNames.contains(POINTS_STORE)) db.createObjectStore(POINTS_STORE, { keyPath: 'id' })
        if (!db.objectStoreNames.contains(OUTBOX_STORE)) db.createObjectStore(OUTBOX_STORE, { keyPath: 'pointId' })
        if (!db.objectStoreNames.contains(KV_STORE)) db.createObjectStore(KV_STORE, { keyPath: 'key' })
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'))
    })
  }
  return dbPromise
}

function requestDone<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

async function withStore<T>(
  store: string,
  mode: IDBTransactionMode,
  work: (objectStore: IDBObjectStore) => Promise<T>,
): Promise<T> {
  const db = await openDb()
  const transaction = db.transaction(store, mode)
  const done = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB transaction failed'))
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB transaction aborted'))
  })
  const result = await work(transaction.objectStore(store))
  await done
  return result
}

// ---------------------------------------------------------------------------
// Points mirror
// ---------------------------------------------------------------------------

export async function putCachedPoints(points: readonly PointDto[]): Promise<void> {
  if (points.length === 0) return
  await withStore(POINTS_STORE, 'readwrite', async (store) => {
    for (const point of points) await requestDone(store.put(point))
  })
}

export async function getCachedPoints(): Promise<PointDto[]> {
  return withStore(POINTS_STORE, 'readonly', async (store) => requestDone<PointDto[]>(store.getAll()))
}

export async function clearCachedPoints(): Promise<void> {
  await withStore(POINTS_STORE, 'readwrite', async (store) => requestDone(store.clear()))
}

// ---------------------------------------------------------------------------
// Outbox
// ---------------------------------------------------------------------------

export async function listOutbox(): Promise<OutboxEntry[]> {
  const entries = await withStore(OUTBOX_STORE, 'readonly', async (store) =>
    requestDone<OutboxEntry[]>(store.getAll()),
  )
  return entries.sort((a, b) => a.queuedAt - b.queuedAt)
}

export async function enqueueChange(entry: OutboxEntry): Promise<void> {
  await withStore(OUTBOX_STORE, 'readwrite', async (store) => requestDone(store.put(entry)))
}

export async function removeOutbox(pointId: string): Promise<void> {
  await withStore(OUTBOX_STORE, 'readwrite', async (store) => requestDone(store.delete(pointId)))
}

/** Wipe every local store — account deletion / sign-out hygiene. */
export async function clearAllLocal(): Promise<void> {
  await withStore(POINTS_STORE, 'readwrite', async (store) => requestDone(store.clear()))
  await withStore(OUTBOX_STORE, 'readwrite', async (store) => requestDone(store.clear()))
  await withStore(KV_STORE, 'readwrite', async (store) => requestDone(store.clear()))
}

// ---------------------------------------------------------------------------
// Key/value meta (last pull cursor etc.)
// ---------------------------------------------------------------------------

interface KvRow<T> {
  key: string
  value: T
}

export async function getKv<T>(key: string): Promise<T | null> {
  const row = await withStore(KV_STORE, 'readonly', async (store) => requestDone<KvRow<T> | undefined>(store.get(key)))
  return row?.value ?? null
}

export async function setKv<T>(key: string, value: T): Promise<void> {
  await withStore(KV_STORE, 'readwrite', async (store) => requestDone(store.put({ key, value } satisfies KvRow<T>)))
}

/** Maps a full point snapshot + its read version into the wire change shape. */
export function buildSyncChange(point: PointDto, baseVersion: number): SyncChange {
  return {
    base_version: baseVersion,
    point: {
      id: point.id,
      title: point.title,
      content_raw: point.content_raw,
      latitude: point.latitude,
      longitude: point.longitude,
      altitude: point.altitude,
      accuracy: point.accuracy,
      waypoint_id: point.waypoint_id,
      deleted_at: point.deleted_at,
    },
  }
}
